// src/components/merchant/ServiceLocationsCard.js
// ============================================================
// LIEUX DE PRESTATION — saisie unique de l'adresse et des horaires
// ------------------------------------------------------------
// Le marchand saisit ici, UNE SEULE FOIS, chaque point de vente :
// adresse exacte, GPS, horaires d'ouverture et consignes d'accueil.
// Ses annonces se contentent ensuite de pointer vers un lieu.
// Modifier un lieu met a jour toutes les annonces rattachees.
// Maxula
// ============================================================

import React, { useEffect, useState } from 'react';
import {
  FiMapPin, FiPlus, FiTrash2, FiSave, FiStar, FiRefreshCw, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { merchantAPI, cityAPI } from '../../services/api';

const emptyLocation = () => ({
  id: '',
  label: '',
  // Ville du lieu : reference vers la table des villes, pas un texte libre.
  cityId: '',
  cityName: '',
  addressFull: '',
  addressLat: '',
  addressLng: '',
  openingHours: '',
  instructions: '',
  isDefault: false
});

const ServiceLocationsCard = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);
  const [openIndex, setOpenIndex] = useState(0);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    // Chargement silencieux : si la liste ne vient pas, le reste du formulaire
    // doit rester utilisable.
    let vivant = true;
    (async () => {
      try {
        const r = await cityAPI.getAll({ limit: 200 });
        const l = r?.data?.data || r?.data || [];
        if (vivant) setCities(Array.isArray(l) ? l : []);
      } catch (e) { /* silencieux */ }
    })();
    return () => { vivant = false; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await merchantAPI.getServiceLocations();
        const list = res?.data?.data;
        setLocations(Array.isArray(list) ? list : []);
      } catch (e) {
        setLocations([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = (index, field, value) => {
    setLocations((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  };

  const setDefault = (index) => {
    setLocations((prev) => prev.map((l, i) => ({ ...l, isDefault: i === index })));
  };

  const addLocation = () => {
    setLocations((prev) => {
      const next = [...prev, { ...emptyLocation(), isDefault: prev.length === 0 }];
      setOpenIndex(next.length - 1);
      return next;
    });
  };

  const removeLocation = (index) => {
    setLocations((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length && !next.some((l) => l.isDefault)) next[0].isDefault = true;
      return next;
    });
  };

  const save = async () => {
    const invalid = locations.findIndex((l) => !String(l.addressFull || '').trim());
    if (invalid >= 0) {
      setOpenIndex(invalid);
      return toast.error('L\'adresse exacte est obligatoire pour chaque lieu');
    }
    try {
      setSaving(true);
      const res = await merchantAPI.saveServiceLocations(locations);
      const list = res?.data?.data;
      if (Array.isArray(list)) setLocations(list);
      toast.success(res?.data?.message || 'Lieux de prestation enregistrés');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = async (loc) => {
    if (!loc?.id) return toast.error('Enregistrez d\'abord ce lieu');
    try {
      setApplying(loc.id);
      const res = await merchantAPI.applyServiceLocation({ locationId: loc.id });
      toast.success(res?.data?.message || 'Annonces mises à jour');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'application');
    } finally {
      setApplying(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-2">Lieux de prestation</h2>
        <p className="text-sm text-gray-500">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <FiMapPin size={16} /> Lieux de prestation
        </h2>
        <button
          type="button"
          onClick={addLocation}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm flex items-center gap-1 hover:bg-gray-50 whitespace-nowrap"
        >
          <FiPlus size={14} /> Ajouter un lieu
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Saisissez ici votre adresse et vos horaires <strong>une seule fois</strong>. Vos annonces
        les reprendront automatiquement, sans avoir à les retaper. Si vous corrigez un lieu,
        toutes les annonces qui l'utilisent sont mises à jour.
      </p>

      {!locations.length && (
        <div className="border border-dashed border-gray-300 rounded-lg px-4 py-6 text-center">
          <p className="text-sm text-gray-500 mb-3">Aucun lieu enregistré pour l'instant.</p>
          <button
            type="button"
            onClick={addLocation}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center gap-2"
          >
            <FiPlus size={14} /> Créer mon premier lieu
          </button>
        </div>
      )}

      <div className="space-y-3">
        {locations.map((loc, i) => {
          const open = openIndex === i;
          return (
            <div key={loc.id || `new-${i}`} className="border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? -1 : i)}
                  className="flex-1 text-left min-w-0"
                >
                  <span className="font-medium text-gray-900 block truncate">
                    {loc.label || 'Nouveau lieu'}
                  </span>
                  <span className="text-xs text-gray-500 block truncate">
                    {loc.cityName ? `${loc.cityName} · ` : ''}{loc.addressFull || 'Adresse à renseigner'}
                  </span>
                </button>
                {loc.isDefault ? (
                  <span className="text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap">
                    <FiStar size={12} /> Par défaut
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setDefault(i)}
                    title="Définir comme lieu par défaut"
                    className="text-xs text-gray-500 border border-gray-300 px-2 py-1 rounded hover:bg-gray-50 whitespace-nowrap"
                  >
                    Par défaut
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeLocation(i)}
                  title="Supprimer ce lieu"
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                >
                  <FiTrash2 size={15} />
                </button>
                <button type="button" onClick={() => setOpenIndex(open ? -1 : i)} className="p-2 text-gray-400">
                  {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </button>
              </div>

              {open && (
                <div className="border-t border-gray-200 p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom du lieu</label>
                    <input
                      type="text"
                      value={loc.label || ''}
                      onChange={(e) => patch(i, 'label', e.target.value)}
                      placeholder="Ex : Salon Tanger Centre"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                      <b>Ce nom apparaît sur le coupon du client</b>, avec l'adresse ci-dessous.
                      Mettez l'enseigne telle qu'elle est visible sur place.
                    </p>
                  </div>

                  {/* VILLE — liste deroulante sur les villes existantes.
                      Un texte libre ferait de « Tanger », « TANGER » et
                      « Tanger  » trois villes differentes dans les filtres et
                      les statistiques. C'est aussi ce qui permettra, plus tard,
                      d'afficher sur le coupon l'adresse de la ville choisie par
                      le client. */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Ville <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={loc.cityId || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const v = cities.find((c) => String(c.id ?? c._id) === String(id));
                        patch(i, 'cityId', id);
                        patch(i, 'cityName', v ? v.name : '');
                      }}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">— Choisir une ville —</option>
                      {cities.map((c) => (
                        <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>
                      ))}
                    </select>
                    {!loc.cityId && (
                      <p className="text-xs text-amber-700 mt-1">
                        À renseigner : sans ville, ce lieu n'apparaîtra pas dans
                        les recherches par ville.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Adresse exacte <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={loc.addressFull || ''}
                      onChange={(e) => patch(i, 'addressFull', e.target.value)}
                      placeholder="Ex : 12 Rue Beethoven, Résidence Oumnia, Bureau 83, Tanger"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Jamais affichée publiquement : communiquée au client sur son coupon, après paiement.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Horaires d'ouverture</label>
                    <input
                      type="text"
                      value={loc.openingHours || ''}
                      onChange={(e) => patch(i, 'openingHours', e.target.value)}
                      placeholder="Ex : Lun–Sam 10h–19h"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Affichés publiquement sur vos annonces.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Latitude (facultatif)</label>
                      <input
                        type="text"
                        value={loc.addressLat ?? ''}
                        onChange={(e) => patch(i, 'addressLat', e.target.value)}
                        placeholder="35.7595"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Longitude (facultatif)</label>
                      <input
                        type="text"
                        value={loc.addressLng ?? ''}
                        onChange={(e) => patch(i, 'addressLng', e.target.value)}
                        placeholder="-5.8340"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 -mt-1">
                    Coordonnées GPS : permettent au client d'ouvrir l'itinéraire depuis son coupon.
                  </p>

                  <div>
                    <label className="block text-sm font-medium mb-1">Consignes de présentation</label>
                    <textarea
                      rows="3"
                      value={loc.instructions || ''}
                      onChange={(e) => patch(i, 'instructions', e.target.value)}
                      placeholder="Ex : Se présenter 10 minutes avant, demander l'accueil au 1er étage."
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {loc.id ? (
                    <button
                      type="button"
                      onClick={() => applyToAll(loc)}
                      disabled={applying === loc.id}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-60"
                    >
                      <FiRefreshCw size={14} className={applying === loc.id ? 'animate-spin' : ''} />
                      {applying === loc.id ? 'Application…' : 'Appliquer ce lieu à toutes mes annonces'}
                    </button>
                  ) : (
                    <p className="text-xs text-gray-500">Enregistrez pour pouvoir appliquer ce lieu à vos annonces existantes.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!!locations.length && (
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60"
        >
          <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer les lieux'}
        </button>
      )}
    </div>
  );
};

export default ServiceLocationsCard;
